import React, { useState } from "react";
import { FiX, FiVideo, FiGlobe, FiExternalLink, FiSearch } from "react-icons/fi";

const youtubeSlugMap = {
  "two-sum": "KLlXCFG5TnA",
  "add-two-numbers": "wgFPrzTqq00",
  "longest-substring-without-repeating-characters": "wiGpQwVHdE0",
  "median-of-two-sorted-arrays": "q6IEA26hvXc",
  "longest-palindromic-substring": "XYQecbcd6_c",
  "container-with-most-water": "UuiTKBwPgAo",
  "3sum": "jzZnG6G882w",
  "valid-parentheses": "WTzjTskDF3Y",
  "merge-two-sorted-lists": "XIdigk956u0",
  "generate-parentheses": "s9fokUqJ76A",
  "merge-k-sorted-lists": "q5a5OiGbT6Q",
  "search-in-rotated-sorted-array": "U8XENwh8Oy8",
  "combination-sum": "GBKI9VSKdGg",
  "trapping-rain-water": "ZI2z5qqhx48",
  "group-anagrams": "vqd80lJG16o",
  "n-queens": "Ph95IHmHJ5Y",
  "maximum-subarray": "AHZpyENo7k4",
  "spiral-matrix": "BJnMZNwUk1M",
  "jump-game": "Yan0cv2cLy8",
  "merge-intervals": "44H3cEC2foo",
  "unique-paths": "IlE7ai5RLbQ",
  "climbing-stairs": "Y0lT9Fck7qI",
  "edit-distance": "XYi2-LPrwm4",
  "set-matrix-zeroes": "N0MgLvceX7M",
  "sort-colors": "tp8JIuCXBaU",
  "minimum-window-substring": "jSto0O4AJbM",
  "word-search": "pfiQ_PS1g8E",
  "subsets": "REOH2L68k89",
  "word-break": "Sx9NNgInc3A",
  "linked-list-cycle": "gBTe7lFR3vc",
  "lru-cache": "7ABLJ4pu9H7",
  "validate-binary-search-tree": "s6ATEkipzow",
  "binary-tree-level-order-traversal": "6ZnyEApgFYg",
  "maximum-depth-of-binary-tree": "hTM3phVI6YQ",
  "best-time-to-buy-and-sell-stock": "1pkOgXD63yU",
  "word-ladder": "h9iTnkgv05E",
  "single-number": "bYWLJb3vCWY",
  "house-robber": "73r3KWiEvyk",
  "number-of-islands": "pV2kpPD66nE",
  "reverse-linked-list": "G0_I-ZF0S38",
  "course-schedule": "EgI5nU9etnU",
  "coin-change": "H9bfqozjoqs"
};

const DSATutorialModal = ({ question, onClose }) => {
  const [selectedLang, setSelectedLang] = useState("English");

  if (!question) return null;

  const tutorials = question.tutorials || [];
  const currentTutorial = tutorials.find((t) => t.language === selectedLang) || 
                          tutorials.find((t) => t.language === "English") || 
                          tutorials[0];

  const getEmbedUrl = (tutorial, lang) => {
    const slug = question.title.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");

    // 1. Direct Slug Match
    if (youtubeSlugMap[slug] && lang === "English") {
      return `https://www.youtube.com/embed/${youtubeSlugMap[slug]}`;
    }

    // 2. Tutorial Specific Video URL
    if (tutorial && tutorial.videoUrl) {
      let url = tutorial.videoUrl;
      if (url.includes("embed/")) return url;
      if (url.includes("watch?v=")) {
        const videoId = url.split("watch?v=")[1]?.split("&")[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      if (url.includes("youtu.be/")) {
        const videoId = url.split("youtu.be/")[1]?.split("?")[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    // 3. Dynamic YouTube Embed Search for exact problem title
    const searchQuery = encodeURIComponent(`${question.title} LeetCode ${lang} solution`);
    return `https://www.youtube.com/embed?listType=search&list=${searchQuery}`;
  };

  const getYouTubeSearchUrl = () => {
    const query = encodeURIComponent(`${question.title} LeetCode tutorial in ${selectedLang}`);
    return `https://www.youtube.com/results?search_query=${query}`;
  };

  const getChannelForLang = (lang) => {
    switch (lang) {
      case "English": return "NeetCode / striver (English)";
      case "Hindi": return "Apna College / CodeHelp (Hindi)";
      case "Telugu": return "Telugu Coding Hub (Telugu)";
      case "Tamil": return "Tamil Coding Hub (Tamil)";
      default: return "YouTube Creator";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-md animate-fadeIn">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 rounded-2xl w-full max-w-3xl flex flex-col shadow-2xl overflow-hidden text-slate-900 dark:text-slate-100 transition-colors">
        
        {/* Modal Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/80">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 flex items-center justify-center">
              <FiVideo className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">{question.title}</h2>
                <span className="text-xs text-red-600 dark:text-red-400 font-semibold bg-red-50 dark:bg-red-500/10 px-2.5 py-0.5 rounded-full border border-red-200 dark:border-red-500/20">
                  Video Tutorial
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{question.dayTopic} • {question.category}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white flex items-center justify-center transition-all cursor-pointer"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Language Selection Bar & Channel Info */}
        <div className="px-6 pt-4 flex flex-wrap items-center justify-between gap-3 bg-slate-50 dark:bg-slate-900">
          <div className="flex items-center space-x-2">
            <FiGlobe className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Select Tutorial Language:</span>
            <div className="flex items-center space-x-1.5">
              {["English", "Hindi", "Telugu", "Tamil"].map((lang) => {
                const isActive = selectedLang === lang;
                return (
                  <button
                    key={lang}
                    onClick={() => setSelectedLang(lang)}
                    className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                      isActive
                        ? "bg-red-600 text-white shadow-md shadow-red-600/30 ring-2 ring-red-400/50"
                        : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-transparent"
                    }`}
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
          </div>

          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 px-3 py-1 rounded-full border border-slate-200 dark:border-slate-700 shadow-xs">
            📺 Channel: <strong className="text-amber-600 dark:text-amber-300">{getChannelForLang(selectedLang)}</strong>
          </span>
        </div>

        {/* Video Player Box */}
        <div className="p-6">
          <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800">
            <iframe
              key={`${question.title}-${selectedLang}`} // Force re-render iframe on problem & language change
              src={getEmbedUrl(currentTutorial, selectedLang)}
              title={`${question.title} Tutorial in ${selectedLang}`}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        {/* Modal Footer with YouTube Search Link */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/80 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-4">
            <a
              href={question.leetcodeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:underline"
            >
              <span>Solve on LeetCode</span>
              <FiExternalLink className="w-3.5 h-3.5" />
            </a>

            <a
              href={getYouTubeSearchUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1.5 text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:underline"
            >
              <FiSearch className="w-3.5 h-3.5" />
              <span>More {selectedLang} Videos on YouTube</span>
            </a>
          </div>

          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-900 dark:text-white text-xs font-bold transition-all cursor-pointer"
          >
            Close Tutorial
          </button>
        </div>

      </div>
    </div>
  );
};

export default DSATutorialModal;
